import React from 'react';
import i18next from 'i18next';
import Icon from '@mdi/react';
import {
    Box,
    Button,
    Collapse,
    List,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
} from '@mui/material';
import { GlobalVariables } from '../../../../pdf/GlobalVariables';

import {
    mdiAccount,
    mdiBookOpenPageVariantOutline,
    mdiChevronUp,
    mdiCloudOutline,
    mdiCollapseAll,
    mdiCommentText,
    mdiCommentTextOutline,
    mdiExpandAll,
    mdiFormatColorHighlight,
    mdiFormatStrikethrough,
    mdiFormatText,
    mdiFormatTextbox,
    mdiFormatUnderline,
    mdiGesture,
    mdiHelp,
    mdiPaperclip,
    mdiRuler,
    mdiShape,
    mdiShapeSquarePlus,
    mdiSortAscending,
    mdiSortDescending,
    mdiStamper,
    mdiTextureBox,
    mdiTimetable,
    mdiVectorCircle,
    mdiVectorLine,
    mdiVectorPolygon,
    mdiVectorPolyline,
    mdiVectorSquare,
    mdiVolumeVibrate,
} from '@mdi/js';

import type { IIndexable } from '../../../../types';
import {
    compareDates,
    compareNumbers,
    compareStrings,
    toLocaleLongString,
    translatePrettyType,
} from '../../../../utils';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';

import styles from './Annotations.module.scss';
import { useSelector } from 'react-redux';
import { apryseEventSliceSelectedAnnotationIds } from '../../../../Redux/ApryseEventSlice';
import type { Core } from '@pdftron/webviewer';
import { ApryseSDK } from '../../../../pdf/ApryseSDK';
import { eplassSliceAnnotationSidepanelRefresh } from '../../../../Redux/EplassSlice';

import ScrollToSelectedMenuItem from '../Components/ScrollToSelectedMenuItem';

const groupByPageKey = 'groupByPage';
const groupByNameKey = 'groupByName';
const groupByTypeKey = 'groupByType';
const groupByDateKey = 'groupByDate';

/**
 * Describes a tree node.
 */
interface INode<TNode extends INode<TNode>> {
    /**
     * The unique key of the node.
     */
    key: string;
    /**
     * The text of the node.
     */
    text: string;
    /**
     * The child nodes of this node.
     */
    children?: TNode[];
    /**
     * Whether the node is expanded.
     */
    isExpanded?: boolean;
    /**
     * The name of the icon to render.
     */
    icon?: JSX.Element;
}

/**
 * A node of the annotation tree.
 */
interface IAnnotationNode extends INode<IAnnotationNode> {
    /**
     * The annotation instance.
     */
    annotation: Core.Annotations.Annotation;
    isSelected: boolean;
    isExpanded: boolean;
    isRoot: boolean;
    page?: number;
    timestamp?: number;
}

/**
 * Recursively generates the nodes for the given annotation.
 * @param annotation The annotation element.
 * @param path The nesting path.
 * @param expanded The collapse/expand states.
 * @param selected The collapse/expand states.
 * @param children Child nodes
 */
function generateAnnotationNode(
    annotation: Core.Annotations.Annotation,
    key: string,
    isExpanded: boolean,
    active: string[],
    children?: IAnnotationNode[],
): IAnnotationNode {
    return {
        annotation,
        key,
        text: annotation.Author,
        isExpanded: children !== undefined ? isExpanded : false,
        isSelected: children === undefined ? active.includes(annotation.Id) : false,
        icon: children
            ? undefined
            : assignIcon(
                  ApryseSDK.annotPrettyType(annotation),
                  annotation.Color?.toHexString() ?? '#000000',
              ),
        children: children,
        isRoot: children !== undefined,
    };
}

function assignIcon(icon: string, color: string | null | undefined): JSX.Element {
    switch (icon) {
        case 'callout':
            return <Icon path={mdiCommentText} color={color} />;
        case 'caret':
            return <Icon path={mdiChevronUp} color={color} />;
        case 'circle':
            return <Icon path={mdiVectorCircle} color={color} />;
        case 'cloud':
            return <Icon path={mdiCloudOutline} color={color} />;
        case 'fileattachment':
            return <Icon path={mdiPaperclip} color={color} />;
        case 'freetext':
            return <Icon path={mdiFormatTextbox} color={color} />;
        case 'highlight':
            return <Icon path={mdiFormatColorHighlight} color={color} />;
        case 'note':
            return <Icon path={mdiCommentTextOutline} color={color} />;
        case 'pencil':
            return <Icon path={mdiGesture} color={color} />;
        case 'line':
            return <Icon path={mdiVectorLine} color={color} />;
        case 'distance':
            return <Icon path={mdiRuler} color={color} />;
        case 'perimeter':
            return <Icon path={mdiShapeSquarePlus} color={color} />;
        case 'area':
            return <Icon path={mdiTextureBox} color={color} />;
        case 'polygon':
            return <Icon path={mdiVectorPolygon} color={color} />;
        case 'polyline':
            return <Icon path={mdiVectorPolyline} color={color} />;
        case 'square':
            return <Icon path={mdiVectorSquare} color={color} />;
        case 'squiggly':
            return (
                <Icon
                    path={mdiVolumeVibrate}
                    style={{ transform: 'rotate(90deg)' }}
                    color={color}
                />
            );
        case 'stamp':
            return <Icon path={mdiStamper} color={color} />;
        case 'strikeout':
            return <Icon path={mdiFormatStrikethrough} color={color} />;
        case 'textbox':
            return <Icon path={mdiFormatTextbox} color={color} />;
        case 'typewriter':
            return <Icon path={mdiFormatText} color={color} />;
        case 'underline':
            return <Icon path={mdiFormatUnderline} color={color} />;
    }
    return <Icon path={mdiHelp} />;
}

// ToDO: save in redux // const annotsGroupBy: Array<string> | undefined = ctx?.eplassContext?.sidePanels?.annotations?.groupBy;
const annotsGroupBy: Array<string> | undefined = [
    'groupByName',
    'groupByPage',
    'groupByType',
    'groupByDate',
];

/**
 * Component that shows a configurable menu structure in a Microsoft-Office-like structure.
 */
export default () => {
    const activeAnnotationIDs = useSelector(apryseEventSliceSelectedAnnotationIds);
    const forceRefresh = useSelector(eplassSliceAnnotationSidepanelRefresh);

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const [sortBy, setSortBy] = React.useState<'Ascending' | 'Descending'>('Ascending');
    const [groupBy, setGroupBy] = React.useState<string>(
        annotsGroupBy && annotsGroupBy.length > 0 ? annotsGroupBy[0] : groupByNameKey,
    );
    const [expanded, setExpanded] = React.useState<IIndexable<boolean>>({});

    const toggleExpanded = (key: string) => {
        const newValue = {};
        newValue[key] = !expanded[key];
        setExpanded({
            ...expanded,
            ...newValue,
        });
    };

    // The root nodes
    const rootNodes = React.useMemo(() => {
        const rootAnnotations = ApryseSDK.annotationManager
            ? ApryseSDK.annotationManager.getAnnotationsList()
            : [];

        if (!rootAnnotations || rootAnnotations.length == 0) {
            return [];
        }

        const { Annotations } = ApryseSDK.instance.Core;
        const allAnnotations = rootAnnotations.filter(
            c =>
                c.Author !== undefined &&
                !(
                    c instanceof Annotations.HTMLAnnotation ||
                    c instanceof Annotations.FileAttachmentAnnotation ||
                    c instanceof Annotations.PopupAnnotation ||
                    c instanceof Annotations.SignatureWidgetAnnotation
                ),
        );

        if (!allAnnotations || allAnnotations.length == 0) {
            return [];
        }

        const path = [];
        const active = activeAnnotationIDs;
        const results: IAnnotationNode[] = [];
        const newExpaned: IIndexable<boolean> = { ...expanded };
        switch (groupBy) {
            case groupByPageKey: {
                allAnnotations
                    .map(a => a.PageNumber)
                    .filter((value, index, self) => self.indexOf(value) === index)
                    .forEach(idx => {
                        if (newExpaned['root_page_' + idx] === undefined) {
                            newExpaned['root_page_' + idx] = true;
                        }
                        const expanded = newExpaned['root_page_' + idx] ?? false;
                        const root = generateAnnotationNode(
                            allAnnotations[0],
                            'root_page_' + idx,
                            expanded,
                            active,
                            allAnnotations
                                .filter(c => c.PageNumber == idx)
                                .sort((a, b) =>
                                    compareDates(
                                        ApryseSDK.getCreationTime(a),
                                        ApryseSDK.getCreationTime(b),
                                        sortBy === 'Descending',
                                    ),
                                )
                                .map((c, i) =>
                                    generateAnnotationNode(
                                        c,
                                        [...path, i].join('_'),
                                        expanded,
                                        active,
                                    ),
                                ),
                        );
                        root.page = idx;
                        root.text = i18next.t('sidebar.popups.page') + ' ' + idx;
                        results.push(root);
                    });
                break;
            }
            case groupByNameKey: {
                allAnnotations
                    .map(a => a.Author)
                    .filter((value, index, self) => self.indexOf(value) === index)
                    .forEach(idx => {
                        if (newExpaned['root_name_' + idx] === undefined) {
                            newExpaned['root_name_' + idx] = true;
                        }
                        const expanded = newExpaned['root_name_' + idx] ?? false;
                        const root = generateAnnotationNode(
                            allAnnotations[0],
                            'root_name_' + idx,
                            expanded,
                            active,
                            allAnnotations
                                .filter(c => c.Author == idx)
                                .sort((a, b) =>
                                    compareDates(
                                        ApryseSDK.getCreationTime(a),
                                        ApryseSDK.getCreationTime(b),
                                        sortBy === 'Descending',
                                    ),
                                )
                                .map((c, i) =>
                                    generateAnnotationNode(
                                        c,
                                        [...path, i].join('_'),
                                        expanded,
                                        active,
                                    ),
                                ),
                        );
                        root.text = idx;
                        results.push(root);
                    });
                break;
            }
            case groupByTypeKey: {
                allAnnotations
                    .map(a => translatePrettyType(a))
                    .filter((value, index, self) => self.indexOf(value) === index)
                    .forEach(idx => {
                        if (newExpaned['root_type_' + idx] === undefined) {
                            newExpaned['root_type_' + idx] = true;
                        }
                        const expanded = newExpaned['root_type_' + idx] ?? false;
                        const root = generateAnnotationNode(
                            allAnnotations[0],
                            'root_type_' + idx,
                            expanded,
                            active,
                            allAnnotations
                                .filter(c => translatePrettyType(c) == idx)
                                .sort((a, b) =>
                                    compareDates(
                                        ApryseSDK.getCreationTime(a),
                                        ApryseSDK.getCreationTime(b),
                                        sortBy === 'Descending',
                                    ),
                                )
                                .map((c, i) =>
                                    generateAnnotationNode(
                                        c,
                                        [...path, i].join('_'),
                                        expanded,
                                        active,
                                    ),
                                ),
                        );
                        root.text = idx;
                        results.push(root);
                    });
                break;
            }
            case groupByDateKey: {
                allAnnotations
                    .map(a => {
                        const d = ApryseSDK.getCreationTime(a);
                        if (!d) {
                            return -1;
                        }
                        d.setHours(0, 0, 0, 0);
                        return d.getTime();
                    })
                    .filter((value, index, self) => self.indexOf(value) === index)
                    .forEach(idx => {
                        if (newExpaned['root_timestamp_' + idx] === undefined) {
                            newExpaned['root_timestamp_' + idx] = true;
                        }
                        const expanded = newExpaned['root_timestamp_' + idx] ?? false;
                        const root = generateAnnotationNode(
                            allAnnotations[0],
                            'root_timestamp_' + idx,
                            expanded,
                            active,
                            allAnnotations
                                .filter(c => {
                                    const d = ApryseSDK.getCreationTime(c);
                                    if (!d) {
                                        return false;
                                    }
                                    d.setHours(0, 0, 0, 0);
                                    return d.getTime() == idx;
                                })
                                .sort((a, b) =>
                                    compareDates(
                                        ApryseSDK.getCreationTime(a),
                                        ApryseSDK.getCreationTime(b),
                                        sortBy === 'Ascending',
                                    ),
                                )
                                .map((c, i) =>
                                    generateAnnotationNode(
                                        c,
                                        [...path, i].join('_'),
                                        expanded,
                                        active,
                                    ),
                                ),
                        );
                        root.text = toLocaleLongString(new Date(idx));
                        root.timestamp = idx;
                        results.push(root);
                    });
                break;
            }
        }

        // Save updates
        setExpanded(newExpaned);

        switch (groupBy + sortBy) {
            case groupByDateKey + 'Ascending':
                return results.sort((a, b) => compareNumbers(a.timestamp, b.timestamp, false));
            case groupByDateKey + 'Descending':
                return results.sort((a, b) => compareNumbers(a.timestamp, b.timestamp, true));

            case groupByNameKey + 'Ascending':
            case groupByTypeKey + 'Ascending':
                return results.sort((a, b) => compareStrings(a.text, b.text));
            case groupByNameKey + 'Descending':
            case groupByTypeKey + 'Descending':
                return results.sort((a, b) => compareStrings(b.text, a.text));

            case groupByPageKey + 'Ascending':
                return results.sort((a, b) => compareNumbers(a.page, b.page, false));
            case groupByPageKey + 'Descending':
                return results.sort((a, b) => compareNumbers(a.page, b.page, true));
        }

        return results;
    }, [forceRefresh, groupBy, sortBy]);

    const sortBarItems = React.useMemo(() => {
        return [
            <Button
                id={GlobalVariables.isForTestRun ? 'annotsSidebarMenuBtnSortAscending' : undefined}
                className={[
                    styles.iconButton,
                    sortBy === 'Ascending' ? styles.iconButtonSelected : null,
                ].join(' ')}
                variant="outlined"
                key="sortAscending"
                title={i18next.t('sidebar.popups.annotations.sort.ascending')}
                onClick={() => {
                    if (sortBy !== 'Ascending') {
                        setSortBy('Ascending');
                    }
                }}
            >
                <Icon path={mdiSortAscending} />
            </Button>,
            <Button
                id={GlobalVariables.isForTestRun ? 'annotsSidebarMenuBtnSortDescending' : undefined}
                className={[
                    styles.iconButton,
                    sortBy === 'Descending' ? styles.iconButtonSelected : null,
                ].join(' ')}
                variant="outlined"
                key="sortDescending"
                title={i18next.t('sidebar.popups.annotations.sort.descending')}
                onClick={() => {
                    if (sortBy !== 'Descending') {
                        setSortBy('Descending');
                    }
                }}
            >
                <Icon path={mdiSortDescending} />
            </Button>,
            <Button
                id={GlobalVariables.isForTestRun ? 'annotsSidebarMenuBtnExpand' : undefined}
                className={styles.iconButton}
                variant="outlined"
                key="expand"
                title={i18next.t('sidebar.popups.annotations.action.expand')}
                onClick={() => {
                    const newExpaned: IIndexable<boolean> = { ...expanded };
                    rootNodes.forEach(n => {
                        newExpaned[n.key] = true;
                    });
                    setExpanded(newExpaned);
                }}
            >
                <Icon path={mdiExpandAll} />
            </Button>,
            <Button
                id={GlobalVariables.isForTestRun ? 'annotsSidebarMenuBtnCollapse' : undefined}
                className={styles.iconButton}
                variant="outlined"
                key="collapse"
                title={i18next.t('sidebar.popups.annotations.action.collapse')}
                onClick={() => {
                    const newExpaned: IIndexable<boolean> = { ...expanded };
                    rootNodes.forEach(n => {
                        newExpaned[n.key] = false;
                    });
                    setExpanded(newExpaned);
                }}
            >
                <Icon path={mdiCollapseAll} />
            </Button>,
        ];
    }, [sortBy, rootNodes]);

    const groupByItemsButton = (by: string, path: string, text: string) => {
        return (
            <MenuItem
                id={GlobalVariables.isForTestRun ? `annotsSidebarMenuBtnGroupBy${by}` : undefined}
                key={by}
                className={styles.iconButton}
                onClick={() => {
                    setGroupBy(by);
                    setAnchorEl(null);
                }}
            >
                <ListItemIcon>
                    <Icon path={path} />
                </ListItemIcon>
                <ListItemText>{text}</ListItemText>
            </MenuItem>
        );
    };

    const groupByItems = React.useMemo(() => {
        const groupSortItems = [
            groupByItemsButton(
                groupByNameKey,
                mdiAccount,
                i18next.t('sidebar.popups.annotations.groupBy.name'),
            ),
            groupByItemsButton(
                groupByPageKey,
                mdiBookOpenPageVariantOutline,
                i18next.t('sidebar.popups.annotations.groupBy.page'),
            ),
            groupByItemsButton(
                groupByTypeKey,
                mdiShape,
                i18next.t('sidebar.popups.annotations.groupBy.type'),
            ),
            groupByItemsButton(
                groupByDateKey,
                mdiTimetable,
                i18next.t('sidebar.popups.annotations.groupBy.date'),
            ),
        ];

        // ToDO: Fix preset sorting
        // if (annotsGroupBy && annotsGroupBy.length > 0) {  // Sort the menu according to config, if config exists
        //   return annotsGroupBy
        //     .map(key => groupSortItems.find(item => key === item.key))
        //     .filter(Boolean);
        // }
        return groupSortItems; // Return default if config does not exist
    }, [groupBy]);

    const currentGroupByIcon = React.useMemo(() => {
        switch (groupBy) {
            case groupByPageKey:
                return <Icon path={mdiBookOpenPageVariantOutline} />;
            case groupByTypeKey:
                return <Icon path={mdiShape} />;
            case groupByNameKey:
                return <Icon path={mdiAccount} />;
            case groupByDateKey:
                return <Icon path={mdiTimetable} />;
            default:
                return null;
        }
    }, [groupBy]);

    const menu = React.useMemo(() => {
        return (
            <Stack className={styles.sidebarMenuArea} direction="row" spacing={1} pl={2} pr={2}>
                <Button
                    id={GlobalVariables.isForTestRun ? 'annotsSidebarMenuBtnGroupBy' : undefined}
                    className={styles.iconButton}
                    onClick={(event: React.MouseEvent<HTMLElement>) =>
                        setAnchorEl(event.currentTarget)
                    }
                    endIcon={<KeyboardArrowDown style={{ borderLeft: '1px solid black' }} />}
                    variant="outlined"
                >
                    {currentGroupByIcon}
                </Button>

                {sortBarItems}

                <Menu anchorEl={anchorEl} id="groupBy-menu" open={Boolean(anchorEl)}>
                    {groupByItems}
                </Menu>
            </Stack>
        );
    }, [anchorEl, sortBarItems]);

    const listElements = React.useMemo(() => {
        const elements: JSX.Element[] = [];
        rootNodes.forEach((node, idx) => {
            elements.push(
                <MenuItem
                    className={styles.sidebarMainListItem}
                    key={`listButton-${idx}`}
                    onClick={() => toggleExpanded(node.key)}
                >
                    {expanded[node.key] ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                    <Stack direction="row" display="contents">
                        <ListItemText
                            primary={node.text}
                            secondaryTypographyProps={{ align: 'right', color: 'red' }}
                        />
                        <span
                            style={{
                                textAlign: 'center',
                                color: 'red',
                                border: '1px solid red',
                                borderRadius: '50%',
                                height: '1.5em',
                                width: '1.5em',
                                fontSize: '12px',
                            }}
                        >
                            {node.children?.length}
                        </span>
                    </Stack>
                </MenuItem>,
            );
            elements.push(
                <Collapse
                    key={`listCollapse-${idx}`}
                    in={expanded[node.key]}
                    timeout="auto"
                    unmountOnExit
                >
                    <List component="div">
                        {node.children?.map(child => (
                            <ScrollToSelectedMenuItem
                                key={`listScrollableCollapse-${child.key}`}
                                isChosen={activeAnnotationIDs.includes(child.annotation.Id)}
                                child={child}
                            />
                        ))}
                    </List>
                </Collapse>,
            );
        });
        return elements;
    }, [rootNodes, expanded, activeAnnotationIDs]);

    return (
        <Stack sx={{ flexGrow: 1 }}>
            <Box>{menu}</Box>
            <Stack className={styles.navWrapper}>
                <List
                    className={styles.resultList}
                    component="nav"
                    sx={{
                        overflowX: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'absolute',
                        top: '0',
                    }}
                >
                    {listElements}
                </List>
            </Stack>
        </Stack>
    );
};
